import { gql, useMutation } from '@apollo/client';
// The component library has no Dialog; Radix is what the old terms pop-up used.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Button, Callout, Dialog, Flex, Text } from '@radix-ui/themes';
import { use, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { denyConsent, grantConsent } from '../../analytics/consent';
import { HUBERT_VIOLET } from '../consent/brandColors';
import { isImpersonatedSession } from '../accesstoken';
import LocaleContext from '../hooks/LocaleContext';
import type { SetUserVariables } from '../hooks/LocaleContext';
import LanguageGlobe from '../navbar/LanguageGlobe';

const PRIVACY_POLICY_URL = 'https://www.hubert.ai/privacy-policy';

/**
 * The privacy window: asks for consent to analytics and links to the privacy policy. The policy
 * is information, not something to accept, so there is no "I have read" box. Accept and Decline
 * look the same; either one closes the window and records that the recruiter saw the current
 * policy (termsAcceptedAt). Under "Login as" there is no analytics question and nothing can be
 * accepted for the customer: the notice explains why, and the only button signs out.
 */
export const TermsModal = ({
  setUserVariables,
  showPolicy = true,
  showAnalytics = !isImpersonatedSession(),
  policyUpdated = true,
}: {
  setUserVariables: SetUserVariables;
  // Defaults are for the old /terms address, which opens the window on its own.
  showPolicy?: boolean;
  showAnalytics?: boolean;
  /** The recruiter saw an earlier version, so the link says "updated". */
  policyUpdated?: boolean;
}) => {
  const { t } = useTranslation();
  const { logout } = use(LocaleContext);
  // Hubert staff using "Login as" (impersonatedBy in the login token) can't accept for the customer.
  const impersonated = isImpersonatedSession();
  const [acceptTerms, { data, loading }] = useMutation(gql`
    mutation acceptTerms {
      acceptTerms {
        settings {
          termsAcceptedAt
        }
      }
    }
  `);

  useEffect(() => {
    if (!data) return;
    setUserVariables?.({ user: { settings: { termsAcceptedAt: data.acceptTerms.settings.termsAcceptedAt } } });
  }, [data, setUserVariables]);

  const finish = (analytics: 'allow' | 'decline') => {
    if (analytics === 'allow') grantConsent('privacy_window');
    if (analytics === 'decline') denyConsent();
    if (showPolicy) acceptTerms().catch(() => toast.error(t('dashboardLayout.error.message')));
  };

  return (
    <Dialog.Root open={true}>
      {/* No auto-focus: a focus ring on Accept would make it stand out from Decline. */}
      <Dialog.Content maxWidth="480px" aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()}>
        <Dialog.Title>
          {t(showAnalytics ? 'privacyDialog.analyticsHeading' : 'privacyDialog.policyHeading')}
        </Dialog.Title>
        <Text as="p" size="2">
          {t(showAnalytics ? 'privacyDialog.analyticsBody' : 'privacyDialog.policyBody')}{' '}
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="text-inherit underline">
            {t(policyUpdated ? 'privacyDialog.policyLinkUpdated' : 'privacyDialog.policyLink')}
          </a>
        </Text>

        {impersonated && (
          <Callout.Root color="amber" className="mt-4">
            <Callout.Text className="mb-0">{t('terms.impersonated')}</Callout.Text>
          </Callout.Root>
        )}

        <Flex gap="3" mt="5" align="center">
          {showAnalytics ? (
            (['allow', 'decline'] as const).map((choice) => (
              <Button
                key={choice}
                size="2"
                className="min-w-28!"
                style={HUBERT_VIOLET}
                loading={loading}
                onClick={() => finish(choice)}
              >
                {t(`analyticsConsent.${choice}`)}
              </Button>
            ))
          ) : (
            <Button size="2" variant="outline" className="min-w-28! cursor-pointer!" onClick={() => logout?.()}>
              {t('nav.userMenu.signOut')}
            </Button>
          )}
          {/* The navbar's language switcher is behind the overlay, so offer it here too. */}
          <LanguageGlobe onLight />
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};
